import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, signal, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { ButtonV3Component, InputV3Component, SelectOption, SelectV2Module } from '@gea/digital-ui-lib';
import { Popover } from 'primeng/popover';
import { Skeleton } from 'primeng/skeleton';
import { WorktreeInfo } from '../data-maintenance.model';

@Component({
  selector: 'app-workspace-popover',
  imports: [ReactiveFormsModule, ButtonV3Component, InputV3Component, SelectV2Module, Popover, Skeleton],
  templateUrl: './workspace-popover.component.html',
  styleUrl: './workspace-popover.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorkspacePopoverComponent {
  @Input() worktrees: WorktreeInfo[] = [];
  @Input() repositories: SelectOption[] = [];
  @Input() activeWorktree: string | null = null;
  @Input() isLoadingWorkspaces = false;
  @Input() isCreatingWorkspace = false;
  @Input() isCopyingWorkspace = false;
  @Input() deletingWorkspace: string | null = null;
  @Input() remoteWorkspaceOptions: SelectOption[] = [];
  @Input() selectedRemoteWorkspace: SelectOption | null = null;

  @Output() createWorkspace = new EventEmitter<string>();
  @Output() selectWorkspace = new EventEmitter<string>();
  @Output() copyWorkspace = new EventEmitter<{ source: string; destination: string }>();
  @Output() deleteWorkspace = new EventEmitter<string>();

  @ViewChild('workspacePopoverRef') private popoverRef!: Popover;

  isOpen = signal(false);
  copyingWorkspace = signal<string | null>(null);

  private readonly workspaceNamePattern = /^(?!\/)(?!.*\/\/)(?!.*\.\.)[a-zA-Z0-9_\-\/]+(?<!\/)(?<!\.lock)(?<!\.)$/;

  private duplicateWorkspaceValidator = (control: AbstractControl): ValidationErrors | null => {
    const value = (control.value ?? '').trim();
    if (!value) return null;
    return this.worktrees.some((w) => w.worktree.toLowerCase() === value.toLowerCase())
      ? { custom: 'A workspace with this name already exists!' }
      : null;
  };

  workspaceForm = this.formBuilder.group({
    name: ['', [Validators.required, Validators.pattern(this.workspaceNamePattern), this.duplicateWorkspaceValidator]],
  });

  copyWorkspaceForm = this.formBuilder.group({
    name: ['', [Validators.required, Validators.pattern(this.workspaceNamePattern), this.duplicateWorkspaceValidator]],
  });

  constructor(private readonly formBuilder: FormBuilder) {}

  get displayWorkspaces(): WorktreeInfo[] {
    return this.worktrees;
  }

  repositoryDisplayName(repositoryKey: string): string | undefined {
    return this.repositories.find((repository) => repository.value.trim().toLowerCase() === repositoryKey.trim().toLowerCase())
      ?.name;
  }

  togglePopover(event: Event): void {
    this.popoverRef.toggle(event);
  }

  hidePopover(): void {
    this.popoverRef.hide();
  }

  clearCreateForm(): void {
    this.workspaceForm.reset();
  }

  resetOnClose(): void {
    this.workspaceForm.reset();
    this.isOpen.set(false);
    this.cancelCopyWorkspace();
  }

  addWorkspace(): void {
    if (this.workspaceForm.invalid || this.isCreatingWorkspace) {
      this.workspaceForm.markAllAsTouched();
      return;
    }
    const name = this.workspaceForm.getRawValue().name?.trim();
    if (!name) return;
    this.createWorkspace.emit(name);
  }

  onSelectWorkspace(workspace: string): void {
    if (workspace !== this.activeWorktree) {
      this.selectWorkspace.emit(workspace);
    }
    this.popoverRef.hide();
  }

  startCopyWorkspace(workspace: string): void {
    this.copyingWorkspace.set(workspace);
    this.copyWorkspaceForm.reset({ name: `${workspace}_copy` });
    // Focus (and place the caret at the end of) the input once it has been rendered.
    setTimeout(() => {
      const input = document.getElementById('copyWorkspaceName') as HTMLInputElement | null;
      input?.focus();
      input?.setSelectionRange(input.value.length, input.value.length);
    });
  }

  cancelCopyWorkspace(): void {
    this.copyingWorkspace.set(null);
    this.copyWorkspaceForm.reset();
  }

  confirmCopyWorkspace(sourceWorkspace: string): void {
    if (this.copyWorkspaceForm.invalid || this.isCopyingWorkspace) {
      this.copyWorkspaceForm.markAllAsTouched();
      return;
    }
    const destinationWorkspace = this.copyWorkspaceForm.getRawValue().name?.trim();
    if (!destinationWorkspace) return;
    this.copyWorkspace.emit({ source: sourceWorkspace, destination: destinationWorkspace });
  }

  isDeleteDisabled(workspace: string): boolean {
    return workspace === this.activeWorktree || (this.deletingWorkspace !== null && this.deletingWorkspace !== workspace);
  }

  onDeleteWorkspaceClick(workspace: string): void {
    if (this.isDeleteDisabled(workspace)) return;
    this.deleteWorkspace.emit(workspace);
  }
}
